import React from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import { Sparkles } from 'lucide-react';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '../../../hooks/useTheme';

const Size = Quill.import('attributors/style/size');
Size.whitelist = ['12px', '14px', '16px', '18px', '20px'];
Quill.register(Size, true);

// Class-based font — avoids whitelist validation bug with multi-word names in style-based attributor
const Font = Quill.import('formats/font');
Font.whitelist = ['Arial', 'CourierNew', 'Georgia', 'Helvetica', 'Impact', 'Tahoma', 'TimesNewRoman', 'TrebuchetMS', 'Verdana'];
Quill.register(Font, true);

const QUILL_MODULES = {
    toolbar: {
        container: [
            ['undo', 'redo'],
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            [{ font: ['Arial', 'CourierNew', 'Georgia', 'Helvetica', 'Impact', 'Tahoma', 'TimesNewRoman', 'TrebuchetMS', 'Verdana'] }],
            [{ size: ['12px', '14px', '16px', '18px', '20px'] }],
            [{ color: [] }, { background: [] }],
        ],
        handlers: {
            undo() { this.quill.history.undo(); },
            redo() { this.quill.history.redo(); },
        },
    },
    history: { delay: 1000, maxStack: 100, userOnly: true },
};

const EmailBodyInput = React.forwardRef(({
    value,
    onChange,
    placeholder = 'Write your email body…',
    // Opt-in: only the wizard's step editor passes these, so the classic modal and the
    // preview keep the plain toolbar.
    onGenerateWithAI = null,
    aiBusy = false,
}, ref) => {
    const { theme } = useTheme();
    const containerRef = React.useRef(null);
    const quillRef = React.useRef(null);
    // Mount point for the AI pill, appended into Quill's own toolbar row after it renders.
    // A portal (rather than manual DOM) keeps the button's busy/disabled state in React
    // while still letting it live inside a DOM node Quill owns.
    const [aiSlot, setAiSlot] = React.useState(null);
    const aiEnabled = !!onGenerateWithAI;

    React.useEffect(() => {
        // Depends on the boolean, never on the handler itself — an inline arrow prop would
        // change identity every render and this effect re-runs setState.
        if (!aiEnabled) return undefined;
        const toolbar = containerRef.current?.querySelector('.ql-toolbar');
        if (!toolbar) return undefined;
        const slot = document.createElement('span');
        // Deliberately not `ql-` prefixed: Quill's toolbar module treats a ql-* class on a
        // toolbar child as a format name and throws when it can't resolve one.
        slot.className = 'ai-gen-slot';
        toolbar.appendChild(slot);
        setAiSlot(slot);
        return () => {
            slot.remove();
            setAiSlot(null);
        };
    }, [aiEnabled]);

    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const reposition = () => {
            container.querySelectorAll('.ql-picker.ql-expanded').forEach(picker => {
                const label = picker.querySelector('.ql-picker-label');
                const opts  = picker.querySelector('.ql-picker-options');
                if (!label || !opts) return;
                const rect = label.getBoundingClientRect();
                // Close if label has scrolled out of the viewport
                if (rect.bottom < 0 || rect.top > window.innerHeight) {
                    picker.classList.remove('ql-expanded');
                    opts.removeAttribute('style');
                    return;
                }
                opts.style.position = 'fixed';
                opts.style.top      = `${rect.bottom + 4}px`;
                opts.style.left     = `${rect.left}px`;
                opts.style.zIndex   = '9999';
                opts.style.maxHeight = 'none';
                opts.style.minWidth  = `${rect.width}px`;
            });
        };

        const closeOnOutsideClick = (e) => {
            container.querySelectorAll('.ql-picker.ql-expanded').forEach(picker => {
                if (!picker.contains(e.target)) {
                    picker.classList.remove('ql-expanded');
                    const opts = picker.querySelector('.ql-picker-options');
                    if (opts) opts.removeAttribute('style');
                }
            });
        };

        // Assign tooltip labels
        const tooltipMap = [
            ['.ql-undo',       'Undo'],
            ['.ql-redo',       'Redo'],
            ['.ql-bold',       'Bold'],
            ['.ql-italic',     'Italic'],
            ['.ql-underline',  'Underline'],
            ['.ql-link',       'Link'],
            ['.ql-image',      'Image'],
            ['.ql-color',      'Text Color'],
            ['.ql-background', 'Highlight Color'],
        ];
        tooltipMap.forEach(([sel, label]) => {
            container.querySelector(sel)?.setAttribute('data-tooltip', label);
        });
        container.querySelectorAll('.ql-list').forEach(btn => {
            const val = btn.getAttribute('value');
            btn.setAttribute('data-tooltip', val === 'ordered' ? 'Numbered List' : 'Bullet List');
        });

        // JS floating tooltip — CSS ::after is clipped by toolbar overflow-x: auto
        const tooltipEl = document.createElement('div');
        Object.assign(tooltipEl.style, {
            position: 'fixed',
            background: '#1f2937',
            color: '#fff',
            fontSize: '11px',
            padding: '3px 7px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            zIndex: '10000',
            pointerEvents: 'none',
            display: 'none',
            transform: 'translateX(-50%)',
        });
        document.body.appendChild(tooltipEl);

        const showTip = (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            tooltipEl.textContent = e.currentTarget.getAttribute('data-tooltip');
            tooltipEl.style.display = 'block';
            tooltipEl.style.left = `${rect.left + rect.width / 2}px`;
            tooltipEl.style.top  = `${rect.top - tooltipEl.offsetHeight - 6}px`;
        };
        const hideTip = () => { tooltipEl.style.display = 'none'; };

        const tooltipTargets = [];
        container.querySelectorAll('[data-tooltip]').forEach(el => {
            el.addEventListener('mouseenter', showTip);
            el.addEventListener('mouseleave', hideTip);
            tooltipTargets.push(el);
        });

        // Prevent Quill's blue hover on color picker icons via inline !important styles
        // (CSS rules alone can't reliably override Quill's snow hover specificity in all browsers)
        const grayColor = theme === 'dark' ? '#9ca3af' : '#6b7280';
        const cpHandlers = [];
        container.querySelectorAll('.ql-color-picker .ql-picker-label').forEach(label => {
            const forceGray = () => {
                label.querySelectorAll('.ql-stroke:not(.ql-color-label)').forEach(el => {
                    el.style.setProperty('stroke', grayColor, 'important');
                });
                label.querySelectorAll('.ql-fill:not(.ql-color-label)').forEach(el => {
                    el.style.setProperty('fill', grayColor, 'important');
                });
            };
            const restoreColor = () => {
                label.querySelectorAll('.ql-stroke:not(.ql-color-label)').forEach(el => el.style.removeProperty('stroke'));
                label.querySelectorAll('.ql-fill:not(.ql-color-label)').forEach(el => el.style.removeProperty('fill'));
            };
            label.addEventListener('mouseenter', forceGray);
            label.addEventListener('mouseleave', restoreColor);
            cpHandlers.push({ label, forceGray, restoreColor });
        });

        // Sync font/size pickers when cursor moves to differently-formatted text.
        // Quill's built-in picker.update() is unreliable for class-based font formats,
        // so we manually set data-value on the picker label from getFormat().
        const quill = quillRef.current?.getEditor?.();
        const onSelectionChange = (range) => {
            if (!range) return;
            const formats = quill.getFormat(range);
            const fontLabel = container.querySelector('.ql-font .ql-picker-label');
            if (fontLabel) fontLabel.setAttribute('data-value', formats.font || '');
            const sizeLabel = container.querySelector('.ql-size .ql-picker-label');
            if (sizeLabel) sizeLabel.setAttribute('data-value', formats.size || '');
        };
        if (quill) quill.on('selection-change', onSelectionChange);

        // Enable/disable undo & redo buttons based on history stack
        const undoBtn = container.querySelector('.ql-undo');
        const redoBtn = container.querySelector('.ql-redo');
        const updateHistoryButtons = () => {
            if (!quill) return;
            if (undoBtn) undoBtn.disabled = quill.history.stack.undo.length === 0;
            if (redoBtn) redoBtn.disabled = quill.history.stack.redo.length === 0;
        };
        if (quill) {
            updateHistoryButtons();
            quill.on('text-change', updateHistoryButtons);
        }

        const observer = new MutationObserver(reposition);
        observer.observe(container, { subtree: true, attributes: true, attributeFilter: ['class'] });
        document.addEventListener('click', closeOnOutsideClick, true);
        window.addEventListener('scroll', reposition, true);

        return () => {
            observer.disconnect();
            document.removeEventListener('click', closeOnOutsideClick, true);
            window.removeEventListener('scroll', reposition, true);
            if (quill) {
                quill.off('selection-change', onSelectionChange);
                quill.off('text-change', updateHistoryButtons);
            }
            tooltipTargets.forEach(el => {
                el.removeEventListener('mouseenter', showTip);
                el.removeEventListener('mouseleave', hideTip);
            });
            cpHandlers.forEach(({ label, forceGray, restoreColor }) => {
                label.removeEventListener('mouseenter', forceGray);
                label.removeEventListener('mouseleave', restoreColor);
            });
            tooltipEl.remove();
        };
    }, []);

    return (
        <div ref={containerRef}>
            <style>{`
                .quill-custom .ql-toolbar {
                    border-radius: 6px 6px 0 0;
                    border-color: ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'} !important;
                    background: ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb'};
                }
                .quill-custom .ql-container {
                    border-radius: 0 0 6px 6px;
                    border-color: ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'} !important;
                    background: ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff'};
                    font-size: 14px;
                    min-height: 220px;
                    font-family: inherit;
                }
                .quill-custom .ql-editor {
                    min-height: 220px;
                    color: ${theme === 'dark' ? '#d1d5db' : '#111827'};
                    line-height: 1.6;
                }
                .quill-custom .ql-editor.ql-blank::before {
                    color: #9ca3af;
                    font-style: normal;
                }
                .quill-custom .ql-stroke:not(.ql-color-label) { stroke: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
                .quill-custom .ql-fill:not(.ql-color-label)   { fill:   ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
                .quill-custom .ql-picker  { color:  ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
                .quill-custom .ql-picker-options {
                    background: ${theme === 'dark' ? '#1e2433' : '#fff'} !important;
                    border-color: ${theme === 'dark' ? 'rgba(75,85,99,0.5)' : '#d1d5db'} !important;
                }
                .quill-custom .ql-picker-item { color: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
                .quill-custom .ql-snow .ql-picker-item.ql-selected { color: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
                .quill-custom .ql-snow .ql-picker-label.ql-active,
                .quill-custom .ql-snow .ql-picker.ql-expanded .ql-picker-label { color: ${theme === 'dark' ? '#9ca3af' : '#374151'} !important; }
                .quill-custom .ql-snow .ql-picker-label:hover { color: ${theme === 'dark' ? '#9ca3af' : '#374151'} !important; }
                .quill-custom .ql-snow .ql-picker-label:hover .ql-stroke:not(.ql-color-label) { stroke: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }
                .quill-custom .ql-snow .ql-picker-label:hover .ql-fill:not(.ql-color-label)   { fill:   ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important; }

                /* Undo / Redo icons. These are text glyphs, not the SVGs the ql-stroke/ql-fill
                   rules above recolour — so they need their own colour or they keep Quill's
                   near-black default and disappear into a dark toolbar. */
                .quill-custom .ql-toolbar button.ql-undo,
                .quill-custom .ql-toolbar button.ql-redo {
                    color: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important;
                }
                .quill-custom .ql-undo::before { content: '↩'; font-size: 13px; }
                .quill-custom .ql-redo::before { content: '↪'; font-size: 13px; }
                .quill-custom .ql-toolbar button.ql-undo:hover,
                .quill-custom .ql-toolbar button.ql-redo:hover {
                    color: ${theme === 'dark' ? '#9ca3af' : '#6b7280'} !important;
                    background: transparent !important;
                }
                .quill-custom .ql-toolbar button.ql-undo:disabled,
                .quill-custom .ql-toolbar button.ql-redo:disabled {
                    opacity: 0.3 !important;
                    cursor: default !important;
                }

                /* Link and image — visible but non-interactive */
                .quill-custom .ql-link,
                .quill-custom .ql-image {
                    opacity: 0.35 !important;
                    pointer-events: none !important;
                    cursor: not-allowed !important;
                }

                /* Force single-row toolbar */
                .quill-custom .ql-toolbar.ql-snow {
                    display: flex;
                    flex-wrap: nowrap;
                    align-items: center;
                    overflow-x: auto;
                    padding: 4px 6px;
                    gap: 2px;
                }
                .quill-custom .ql-toolbar .ql-formats {
                    display: inline-flex;
                    align-items: center;
                    margin-right: 4px !important;
                    gap: 4px;
                }
                .quill-custom .ql-toolbar.ql-snow button {
                    width: 22px;
                    height: 22px;
                    padding: 2px;
                }
                .quill-custom .ql-font.ql-picker  { width: 85px !important; }
                .quill-custom .ql-size.ql-picker  { width: 62px !important; }
                .quill-custom .ql-color-picker { width: 26px !important; padding: 0 !important; }
                .quill-custom .ql-color-picker .ql-picker-label {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    padding: 1px !important;
                    height: 22px !important;
                    border: none !important;
                }
                .quill-custom .ql-color-picker .ql-picker-label svg {
                    width: 20px !important;
                    height: 20px !important;
                }

                /* Size picker: show px value instead of "Normal" */
                .quill-custom .ql-snow .ql-picker.ql-size .ql-picker-label::before {
                    content: attr(data-value) !important;
                    flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .quill-custom .ql-snow .ql-picker.ql-size .ql-picker-label[data-value=""]::before,
                .quill-custom .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before {
                    content: '14px' !important;
                }
                .quill-custom .ql-snow .ql-picker.ql-size .ql-picker-item::before {
                    content: attr(data-value) !important;
                }

                /* Font picker: show actual font name instead of "Sans Serif" */
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-label::before {
                    content: attr(data-value) !important;
                    flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=""]::before,
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-label:not([data-value])::before {
                    content: 'Sans Serif' !important;
                }
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-item::before {
                    content: attr(data-value) !important;
                }
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=""]::before { content: 'Sans Serif' !important; }
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="CourierNew"]::before,
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="CourierNew"]::before   { content: 'Courier New' !important; }
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="TimesNewRoman"]::before,
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="TimesNewRoman"]::before { content: 'Times New Roman' !important; }
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="TrebuchetMS"]::before,
                .quill-custom .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="TrebuchetMS"]::before   { content: 'Trebuchet MS' !important; }

                /* Font classes applied to editor content */
                .quill-custom .ql-editor .ql-font-Arial        { font-family: Arial, sans-serif !important; }
                .quill-custom .ql-editor .ql-font-CourierNew   { font-family: 'Courier New', monospace !important; }
                .quill-custom .ql-editor .ql-font-Georgia      { font-family: Georgia, serif !important; }
                .quill-custom .ql-editor .ql-font-Helvetica    { font-family: Helvetica, Arial, sans-serif !important; }
                .quill-custom .ql-editor .ql-font-Impact       { font-family: Impact, sans-serif !important; }
                .quill-custom .ql-editor .ql-font-Tahoma       { font-family: Tahoma, Geneva, sans-serif !important; }
                .quill-custom .ql-editor .ql-font-TimesNewRoman{ font-family: 'Times New Roman', serif !important; }
                .quill-custom .ql-editor .ql-font-TrebuchetMS  { font-family: 'Trebuchet MS', sans-serif !important; }
                .quill-custom .ql-editor .ql-font-Verdana      { font-family: Verdana, Geneva, sans-serif !important; }

                /* B / I / U active highlight — the light theme's lavender fill is a lit block on
                   a dark toolbar, so dark tints the surface instead and lightens the glyph. */
                .quill-custom .ql-toolbar button.ql-active {
                    background: ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#ede9fe'} !important;
                    border-radius: 3px !important;
                }
                .quill-custom .ql-toolbar button.ql-active .ql-stroke { stroke: ${theme === 'dark' ? '#c4b5fd' : '#7c3aed'} !important; }
                .quill-custom .ql-toolbar button.ql-active .ql-fill   { fill:   ${theme === 'dark' ? '#c4b5fd' : '#7c3aed'} !important; }

                /* Box appearance + down arrow for font and size pickers. Same bordered-but-
                   unfilled treatment as the AI button below — a white fill here reads as two
                   lit panels floating on the dark toolbar. */
                .quill-custom .ql-font .ql-picker-label,
                .quill-custom .ql-size .ql-picker-label {
                    border: 1px solid ${theme === 'dark' ? 'rgba(156,163,175,0.45)' : '#d1d5db'} !important;
                    border-radius: 4px !important;
                    padding: 1px 5px !important;
                    display: flex !important;
                    align-items: center !important;
                    background: ${theme === 'dark' ? 'transparent' : '#fff'} !important;
                    gap: 2px !important;
                }
                .quill-custom .ql-font .ql-picker-label svg,
                .quill-custom .ql-size .ql-picker-label svg {
                    display: none !important;
                }
                .quill-custom .ql-font .ql-picker-label::after,
                .quill-custom .ql-size .ql-picker-label::after {
                    content: '▾' !important;
                    font-size: 13px !important;
                    color: #9ca3af !important;
                    flex-shrink: 0 !important;
                    line-height: 1 !important;
                }

                /* Generate with AI — pinned to the trailing edge of the toolbar row. */
                .quill-custom .ql-toolbar .ai-gen-slot {
                    margin-left: auto;
                    display: inline-flex;
                    align-items: center;
                    flex-shrink: 0;
                    padding-left: 10px;
                }
                /* Every override needs to out-specify Quill's own .ql-toolbar.ql-snow button
                   sizing rule (22x22 icon square) above. */
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn {
                    width: auto !important;
                    height: auto !important;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px !important;
                    border-radius: 9999px;
                    border: 1px solid ${theme === 'dark' ? 'rgba(156,163,175,0.45)' : '#d1d5db'};
                    background: ${theme === 'dark' ? 'transparent' : '#fff'};
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 1.5;
                    white-space: nowrap;
                    cursor: pointer;
                    position: relative;
                    z-index: 0;
                    float: none;
                }
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn:hover {
                    background: ${theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#fff'} !important;
                    border-color: transparent;
                }
                /* Gradient ring on hover — same masked-border technique as the wizard's other
                   outlined pills (gradientAccents.css), inlined so this shared input doesn't
                   depend on the builder's stylesheet being loaded. */
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn:not(:disabled):hover::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    padding: 1.5px;
                    border-radius: inherit;
                    background: linear-gradient(267deg, #ff60b6, #2960fd);
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    pointer-events: none;
                    z-index: -1;
                }
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn:disabled {
                    opacity: 0.55;
                    cursor: default;
                }
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn .ai-gen-icon {
                    color: #FF60B6;
                    flex-shrink: 0;
                }
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn .ai-gen-label {
                    background: linear-gradient(267deg, #ff60b6, #2960fd);
                    -webkit-background-clip: text;
                    background-clip: text;
                    color: transparent;
                }
                .quill-custom .ql-toolbar.ql-snow button.ai-gen-btn .ai-gen-spinner {
                    width: 13px;
                    height: 13px;
                    border: 2px solid #FF60B6;
                    border-top-color: transparent;
                    border-radius: 9999px;
                    animation: ai-gen-spin 0.7s linear infinite;
                    flex-shrink: 0;
                }
                @keyframes ai-gen-spin { to { transform: rotate(360deg); } }
            `}</style>

            {aiSlot && createPortal(
                <button
                    type="button"
                    onClick={onGenerateWithAI}
                    disabled={aiBusy}
                    className="ai-gen-btn"
                    title="Describe this email and let AI draft it"
                >
                    {aiBusy
                        ? <span className="ai-gen-spinner" />
                        : <Sparkles className="ai-gen-icon" style={{ width: 13, height: 13 }} />}
                    <span className="ai-gen-label">{aiBusy ? 'Generating…' : 'Generate with AI'}</span>
                </button>,
                aiSlot
            )}

            <ReactQuill
                ref={(el) => {
                    quillRef.current = el;
                    if (typeof ref === 'function') ref(el);
                    else if (ref) ref.current = el;
                }}
                className="quill-custom"
                theme="snow"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                modules={QUILL_MODULES}
            />

            <p style={{ margin: '6px 0 0', fontSize: '12px', textAlign: 'right' }}
                className="dark:text-gray-500 text-gray-400">
                {(value || '').replace(/<[^>]*>/g, '').length} characters
            </p>
        </div>
    );
});

EmailBodyInput.displayName = 'EmailBodyInput';

export default EmailBodyInput;
